import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Normalize phone number to E.164 format (or consistent format)
 * Handles formats like: +17782236715, 17782236715, 7782236715, (778) 223-6715, etc.
 */
export function normalizePhoneNumber(phone: string): string {
  // Remove all non-numeric characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // If it starts with 1 and has 11 digits, assume US/Canada and keep the 1
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`;
  }
  
  // If it has 10 digits, assume US/Canada and add +1
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }
  
  // If it already starts with +, keep it
  if (phone.trim().startsWith('+')) {
    return phone.trim();
  }
  
  // Otherwise, add + and return
  return `+${digitsOnly}`;
}

/**
 * Normalize email address (lowercase, trim)
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

export interface SecondaryEmailEntry {
  id: string;
  business_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  tags?: string[] | null;
  unsubscribed: boolean;
  created_at: string;
  updated_at: string;
}

export interface SecondaryPhoneEntry {
  id: string;
  business_id: string;
  phone: string;
  first_name?: string | null;
  last_name?: string | null;
  tags?: string[] | null;
  unsubscribed: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get all secondary email entries for a business (with pagination to handle large lists)
 */
export async function getSecondaryEmails(
  supabase: SupabaseClient,
  businessId: string,
  filters?: {
    unsubscribed?: boolean;
    tags?: string[];
  }
): Promise<SecondaryEmailEntry[]> {
  const BATCH_SIZE = 1000;
  let allEntries: SecondaryEmailEntry[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from('secondary_email_lists')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .range(offset, offset + BATCH_SIZE - 1);

    if (filters?.unsubscribed !== undefined) {
      query = query.eq('unsubscribed', filters.unsubscribed);
    }

    if (filters?.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching secondary emails:', error);
      throw error;
    }

    if (data && data.length > 0) {
      allEntries = [...allEntries, ...data];
      offset += BATCH_SIZE;
      hasMore = data.length === BATCH_SIZE;
    } else {
      hasMore = false;
    }
  }

  return allEntries;
}

/**
 * Get all secondary phone entries for a business (with pagination to handle large lists)
 */
export async function getSecondaryPhones(
  supabase: SupabaseClient,
  businessId: string,
  filters?: {
    unsubscribed?: boolean;
    tags?: string[];
  }
): Promise<SecondaryPhoneEntry[]> {
  const BATCH_SIZE = 1000;
  let allEntries: SecondaryPhoneEntry[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from('secondary_phone_lists')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .range(offset, offset + BATCH_SIZE - 1);

    if (filters?.unsubscribed !== undefined) {
      query = query.eq('unsubscribed', filters.unsubscribed);
    }

    if (filters?.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching secondary phones:', error);
      throw error;
    }

    if (data && data.length > 0) {
      allEntries = [...allEntries, ...data];
      offset += BATCH_SIZE;
      hasMore = data.length === BATCH_SIZE;
    } else {
      hasMore = false;
    }
  }

  return allEntries;
}

/**
 * Add secondary email entries (bulk import)
 */
export async function addSecondaryEmails(
  supabase: SupabaseClient,
  businessId: string,
  entries: Array<{
    email: string;
    first_name?: string;
    last_name?: string;
    tags?: string[];
  }>
): Promise<{ success: number; failed: number; errors: string[]; duplicates: number }> {
  // Normalize and deduplicate entries
  const normalizedMap = new Map<string, {
    email: string;
    first_name?: string;
    last_name?: string;
    tags?: string[];
  }>();
  
  let duplicates = 0;
  
  for (const entry of entries) {
    const normalizedEmail = normalizeEmail(entry.email);
    
    // Skip invalid emails
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      continue;
    }
    
    // Check for duplicates in the upload batch
    if (normalizedMap.has(normalizedEmail)) {
      duplicates++;
      // Merge tags if both have them
      const existing = normalizedMap.get(normalizedEmail)!;
      if (entry.tags && entry.tags.length > 0) {
        existing.tags = [...(existing.tags || []), ...entry.tags];
      }
      // Prefer non-empty names
      if (entry.first_name && !existing.first_name) {
        existing.first_name = entry.first_name;
      }
      if (entry.last_name && !existing.last_name) {
        existing.last_name = entry.last_name;
      }
    } else {
      normalizedMap.set(normalizedEmail, {
        email: normalizedEmail,
        first_name: entry.first_name?.trim() || undefined,
        last_name: entry.last_name?.trim() || undefined,
        tags: entry.tags || [],
      });
    }
  }
  
  // Check for existing emails in database
  const normalizedEmails = Array.from(normalizedMap.keys());
  const existingEmails = new Set<string>();
  
  // Check in batches to avoid query limits
  const checkBatchSize = 1000;
  for (let i = 0; i < normalizedEmails.length; i += checkBatchSize) {
    const batch = normalizedEmails.slice(i, i + checkBatchSize);
    const { data } = await supabase
      .from('secondary_email_lists')
      .select('email')
      .eq('business_id', businessId)
      .in('email', batch);
    
    if (data) {
      data.forEach(item => existingEmails.add(item.email));
    }
  }
  
  // Filter out existing emails
  const newEntries = Array.from(normalizedMap.values()).filter(entry => !existingEmails.has(entry.email));
  const newDuplicates = normalizedEmails.length - newEntries.length;
  duplicates += newDuplicates;
  
  if (newEntries.length === 0) {
    return { success: 0, failed: 0, errors: [], duplicates };
  }
  
  const records = newEntries.map(entry => ({
    business_id: businessId,
    email: entry.email,
    first_name: entry.first_name?.trim() || null,
    last_name: entry.last_name?.trim() || null,
    tags: entry.tags || [],
    unsubscribed: false,
  }));

  // Insert in batches to avoid overwhelming the database
  const batchSize = 100;
  let success = 0;
  let failed = 0;
  const errors: string[] = [];

  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);
    const { error } = await supabase
      .from('secondary_email_lists')
      .upsert(batch, { onConflict: 'business_id,email' });

    if (error) {
      console.error(`Error inserting email batch ${i / batchSize + 1}:`, error);
      failed += batch.length;
      errors.push(`Batch ${i / batchSize + 1}: ${error.message}`);
    } else {
      success += batch.length;
    }
  }

  return { success, failed, errors, duplicates };
}

/**
 * Add secondary phone entries (bulk import)
 */
export async function addSecondaryPhones(
  supabase: SupabaseClient,
  businessId: string,
  entries: Array<{
    phone: string;
    first_name?: string;
    last_name?: string;
    tags?: string[];
  }>
): Promise<{ success: number; failed: number; errors: string[]; duplicates: number }> {
  // Normalize and deduplicate entries
  const normalizedMap = new Map<string, {
    phone: string;
    first_name?: string;
    last_name?: string;
    tags?: string[];
  }>();
  
  let duplicates = 0;
  
  for (const entry of entries) {
    const normalizedPhone = normalizePhoneNumber(entry.phone);
    
    // Skip invalid phone numbers (need at least 10 digits)
    const digitsOnly = normalizedPhone.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      continue;
    }
    
    // Check for duplicates in the upload batch
    if (normalizedMap.has(normalizedPhone)) {
      duplicates++;
      // Merge tags if both have them
      const existing = normalizedMap.get(normalizedPhone)!;
      if (entry.tags && entry.tags.length > 0) {
        existing.tags = [...(existing.tags || []), ...entry.tags];
      }
      // Prefer non-empty names
      if (entry.first_name && !existing.first_name) {
        existing.first_name = entry.first_name;
      }
      if (entry.last_name && !existing.last_name) {
        existing.last_name = entry.last_name;
      }
    } else {
      normalizedMap.set(normalizedPhone, {
        phone: normalizedPhone,
        first_name: entry.first_name?.trim() || undefined,
        last_name: entry.last_name?.trim() || undefined,
        tags: entry.tags || [],
      });
    }
  }
  
  // Check for existing phones in database
  const normalizedPhones = Array.from(normalizedMap.keys());
  const existingPhones = new Set<string>();
  
  // Check in batches to avoid query limits
  const checkBatchSize = 1000;
  for (let i = 0; i < normalizedPhones.length; i += checkBatchSize) {
    const batch = normalizedPhones.slice(i, i + checkBatchSize);
    const { data } = await supabase
      .from('secondary_phone_lists')
      .select('phone')
      .eq('business_id', businessId)
      .in('phone', batch);
    
    if (data) {
      data.forEach(item => existingPhones.add(item.phone));
    }
  }
  
  // Filter out existing phones
  const newEntries = Array.from(normalizedMap.values()).filter(entry => !existingPhones.has(entry.phone));
  const newDuplicates = normalizedPhones.length - newEntries.length;
  duplicates += newDuplicates;
  
  if (newEntries.length === 0) {
    return { success: 0, failed: 0, errors: [], duplicates };
  }
  
  const records = newEntries.map(entry => ({
    business_id: businessId,
    phone: entry.phone,
    first_name: entry.first_name?.trim() || null,
    last_name: entry.last_name?.trim() || null,
    tags: entry.tags || [],
    unsubscribed: false,
  }));

  // Insert in batches to avoid overwhelming the database
  const batchSize = 100;
  let success = 0;
  let failed = 0;
  const errors: string[] = [];

  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);
    const { error } = await supabase
      .from('secondary_phone_lists')
      .upsert(batch, { onConflict: 'business_id,phone' });

    if (error) {
      console.error(`Error inserting phone batch ${i / batchSize + 1}:`, error);
      failed += batch.length;
      errors.push(`Batch ${i / batchSize + 1}: ${error.message}`);
    } else {
      success += batch.length;
    }
  }

  return { success, failed, errors, duplicates };
}

/**
 * Delete a secondary email entry
 */
export async function deleteSecondaryEmail(
  supabase: SupabaseClient,
  id: string
): Promise<void> {
  const { error } = await supabase
    .from('secondary_email_lists')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting secondary email:', error);
    throw error;
  }
}

/**
 * Delete a secondary phone entry
 */
export async function deleteSecondaryPhone(
  supabase: SupabaseClient,
  id: string
): Promise<void> {
  const { error } = await supabase
    .from('secondary_phone_lists')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting secondary phone:', error);
    throw error;
  }
}

/**
 * Get count of secondary emails (excluding unsubscribed)
 */
export async function getSecondaryEmailCount(
  supabase: SupabaseClient,
  businessId: string,
  filters?: {
    tags?: string[];
  }
): Promise<number> {
  let query = supabase
    .from('secondary_email_lists')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('unsubscribed', false);

  if (filters?.tags && filters.tags.length > 0) {
    query = query.contains('tags', filters.tags);
  }

  const { count, error } = await query;

  if (error) {
    console.error('Error counting secondary emails:', error);
    return 0;
  }

  return count || 0;
}

/**
 * Get count of secondary phones (excluding unsubscribed)
 */
export async function getSecondaryPhoneCount(
  supabase: SupabaseClient,
  businessId: string,
  filters?: {
    tags?: string[];
  }
): Promise<number> {
  let query = supabase
    .from('secondary_phone_lists')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('unsubscribed', false);

  if (filters?.tags && filters.tags.length > 0) {
    query = query.contains('tags', filters.tags);
  }

  const { count, error } = await query;

  if (error) {
    console.error('Error counting secondary phones:', error);
    return 0;
  }

  return count || 0;
}

